import { Injectable } from '@angular/core';
import { PositionDataService, TableDataService } from '@core/api/data-services';
import { ServingMethod } from '@core/hera-models';
import { TranslateService } from '@ngx-translate/core';
import {
  Placement,
  PlacementPayload,
  placementKindForServingMethod,
  placementToPayload,
  positionToPlacement,
  tableToPlacement,
} from '@shared/interfaces';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, finalize, map, tap } from 'rxjs/operators';
import { SnackBarService } from './snack-bar.service';

/**
 * Orchestrates the guest "placement" for a CAT order.
 *
 * Depending on the shop serving method, a placement is either a table
 * (`SequentialService`), a position (`CompleteService`) or nothing
 * (`CounterPickupService`). This service is the single entry point the rest of
 * the app uses: it delegates to {@link TableDataService} / {@link PositionDataService}
 * and exposes a normalized {@link Placement}.
 */
@Injectable({
  providedIn: 'root',
})
export class PlacementService {
  private readonly placement$ = new BehaviorSubject<Placement | null>(null);

  constructor(
    private tableDataService: TableDataService,
    private positionDataService: PositionDataService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private snackBarService: SnackBarService,
    private translateService: TranslateService
  ) {}

  selectPlacement(): Observable<Placement | null> {
    return this.placement$.asObservable();
  }

  selectExistingPlacement(): Observable<Placement> {
    return this.selectPlacement().pipe(
      filter((placement): placement is Placement => !!placement)
    );
  }

  getPlacement(): Placement | null {
    return this.placement$.getValue();
  }

  clear(): void {
    this.placement$.next(null);
  }

  /**
   * Fetches the table / position matching `placementNumber` for the given
   * serving method and stores it. Shows a "not found" message when nothing
   * matches. Emits `null` (without a call) for serving methods without a
   * placement (counter pickup).
   */
  fetchPlacement(
    shopId: string,
    placementNumber: string,
    servingMethod: ServingMethod | string | null | undefined
  ): Observable<Placement | null> {
    const kind = placementKindForServingMethod(servingMethod);
    if (kind === null) {
      this.placement$.next(null);
      return of(null);
    }

    const request$: Observable<Placement | null> =
      kind === 'position'
        ? this.positionDataService
            .fetchPosition(shopId, placementNumber)
            .pipe(
              map((position) =>
                position ? positionToPlacement(position) : null
              )
            )
        : this.tableDataService
            .fetchTable(shopId, placementNumber)
            .pipe(map((table) => (table ? tableToPlacement(table) : null)));

    return request$.pipe(
      tap((placement) => {
        if (!placement) {
          this.snackBarService.error(
            this.translateService.instant(
              `recapCommandePage.${servingMethod}.tableNotFound`
            )
          );
        }
        this.placement$.next(placement);
      })
    );
  }

  /**
   * Fetches the placement and guarantees it is orderable, throwing otherwise.
   * Drives the global loader while running.
   */
  ensureOrderablePlacement(
    shopId: string,
    placementNumber: string,
    servingMethod: ServingMethod | string | null | undefined
  ): Observable<Placement> {
    this.ngxUiLoaderService.start();
    return this.fetchPlacement(shopId, placementNumber, servingMethod).pipe(
      finalize(() => this.ngxUiLoaderService.stop()),
      map(() => {
        const placement = this.getPlacement();
        if (!placement?.isOrderable) {
          throw new Error('Placement in progress');
        }
        return placement;
      })
    );
  }

  /**
   * Payload fragment (`{ table }`, `{ position }` or `{}`) to merge into a
   * Hera quotation / order creation request.
   */
  toPayload(
    servingMethod: ServingMethod | string | null | undefined
  ): PlacementPayload {
    if (placementKindForServingMethod(servingMethod) === null) {
      return {};
    }
    return placementToPayload(this.getPlacement());
  }
}
